import { Search } from "@mui/icons-material";
import { InputAdornment } from "@mui/material";
import React, { FC, useMemo } from "react";
import { Control } from "react-hook-form";
import {
  FormController,
  MuiBox,
  MuiCheckbox,
  MuiGrid,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
} from "../../../../../../../../../../../../components";
import { MuiSelectedList } from "../../../../../../../../../../../../components/selectedList/MuiSelectedList";
import { MAX_LENGTH_75 } from "../../../../../../../../../../../../helpers/constants";
import {
  getValueOrBlank,
  replaceHashWithValue,
} from "../../../../../../../../../../../../helpers/utils";
import { useNavigationSiteCore } from "../../../../../../../../../../../../hooks/useNavigationSiteCore";
import { useSiteCore } from "../../../../../../../../../../../../hooks/useSiteCore";
import {
  ProjectDeliverablesFormKeys,
  ProjectExistingDeliverableFilterFormProps,
  ProjectExistingDeliverableFormKeys,
  ProjectExistingDeliverablesType,
} from "../../../../modelsAndSchemas/models";
import { addDeliverableSiteCoreKeys } from "../../../../siteCore/SiteCoreKeys";
import { ProjectExistingDeliverableWrapper } from "./ProjectExistingDeliverables.style";

export interface ProjectExistingDeliverableProps {
  onCheckAllChange: (checked: boolean) => void;
  isAllChecked: boolean;
  pageId: string;
  handleRowCheckboxChange: (checked: boolean, id: string) => void;
  existingFilterFormControl: Control<ProjectExistingDeliverableFilterFormProps>;
  tableRef: React.RefObject<{ resetPagination: () => void }>;
  existingDeliverablesList: ProjectExistingDeliverablesType[];
  noDataInApi: boolean;
}
const ProjectExistingDeliverables: FC<ProjectExistingDeliverableProps> = ({
  onCheckAllChange,
  isAllChecked: isAllSelected,
  pageId,
  handleRowCheckboxChange,
  existingFilterFormControl,
  tableRef,
  existingDeliverablesList,
  noDataInApi,
}) => {
  const {
    commonLabels,
    fetchLabel,
    fetchValueByKeyOfDropdown,
    fetchDropdownValues,
  } = useSiteCore();

  const { commonARIALabel } = useNavigationSiteCore();

  const selectedItems = useMemo(() => {
    return existingDeliverablesList
      .filter((item) => item.isSelected)
      .map((item) => ({
        label: getValueOrBlank(item.name),
        value: item.id,
      }));
  }, [existingDeliverablesList]);

  const renderDeliverableFormControl = (
    control: any,
    name: keyof typeof ProjectDeliverablesFormKeys,
    options: OptionsDataType[],
    placeholder: string,
    id: string,
  ) => (
    <FormController control={control} name={name} key={name}>
      <MuiSelect
        label=""
        visibleDefaultSelectOption={true}
        options={options}
        value=""
        id={id}
        placeholder={placeholder}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>
  );

  const columns = [
    {
      id: ProjectExistingDeliverableFormKeys.isSelected,
      label: "",
      className: "checkbox-cell",
      isSortVisible: false,
      headerRender: () => (
        <MuiCheckbox
          checked={isAllSelected}
          checkboxLabel={`${commonARIALabel.selectAll} ${fetchLabel(
            addDeliverableSiteCoreKeys.addDeliverable,
            addDeliverableSiteCoreKeys.deliverableName,
          )}`}
          hideLabel={true}
          onCheckedChange={(checked: boolean) => onCheckAllChange(checked)}
        />
      ),
      render: (row: any) => {
        return (
          <MuiCheckbox
            checked={row.isSelected ?? false}
            checkboxLabel={row.name}
            hideLabel={true}
            id={`${pageId}-${row.id}`}
            onCheckedChange={(checked: boolean) => {
              handleRowCheckboxChange(checked, row.id);
            }}
          />
        );
      },
    },
    {
      id: ProjectExistingDeliverableFormKeys.name,
      label: fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.deliverableName,
      ),
      isSortVisible: true,
      className: "cell-name",
    },

    {
      id: ProjectExistingDeliverableFormKeys.deliverableTypeName,
      label: fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.deliverableType,
      ),
      isSortVisible: true,
    },
    {
      id: ProjectExistingDeliverableFormKeys.unitName,
      label: fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.units,
      ),
      isSortVisible: true,
    },
    {
      id: ProjectExistingDeliverableFormKeys.measurements,
      label: fetchLabel(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.measurement,
      ),
      render: (row: any) => {
        return isNaN(row.measurements)
          ? fetchValueByKeyOfDropdown(
              addDeliverableSiteCoreKeys.addDeliverable,
              addDeliverableSiteCoreKeys.minutesList,
              row.measurements,
            )
          : row.measurements;
      },
      isSortVisible: true,
    },
  ];

  const tableHeaderForm = [
    <></>,
    <FormController
      control={existingFilterFormControl}
      name={ProjectExistingDeliverableFormKeys.name}
      key={ProjectExistingDeliverableFormKeys.name}
    >
      <MuiTextField
        id={`${pageId}-${ProjectExistingDeliverableFormKeys.name}`}
        startAdornment={
          <InputAdornment position="start">
            <Search />
          </InputAdornment>
        }
        aria-label={commonLabels.search}
        showCloseIcon={true}
        maxLength={MAX_LENGTH_75}
        placeholder={commonLabels.search}
        shouldTrimSpaces={false}
        requiredARIALabel={commonARIALabel.required}
      />
    </FormController>,
    renderDeliverableFormControl(
      existingFilterFormControl,
      ProjectExistingDeliverableFormKeys.deliverableType,
      fetchDropdownValues(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.deliverableTypeList,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectExistingDeliverableFormKeys.deliverableType}`,
    ),
    renderDeliverableFormControl(
      existingFilterFormControl,
      ProjectExistingDeliverableFormKeys.units,
      fetchDropdownValues(
        addDeliverableSiteCoreKeys.addDeliverable,
        addDeliverableSiteCoreKeys.unitsList,
      ),
      commonLabels.selectPlaceHolder,
      `${pageId}-${ProjectExistingDeliverableFormKeys.units}`,
    ),
    <></>,
  ];
  return (
    <ProjectExistingDeliverableWrapper>
      <MuiGrid container className="existing-header">
        <MuiGrid size={{ xs: 6 }} className="existing-deliverable ">
          <MuiTypography variant="h2SemiBold" component="h2">
            {fetchLabel(
              addDeliverableSiteCoreKeys.addDeliverable,
              addDeliverableSiteCoreKeys.existingDeliverablesHeading,
            )}
          </MuiTypography>
        </MuiGrid>
        <MuiSelectedList
          selectedItems={selectedItems}
          label={replaceHashWithValue(
            fetchLabel(
              addDeliverableSiteCoreKeys.addDeliverable,
              addDeliverableSiteCoreKeys.deliverablesSelected,
            ),
            String(selectedItems.length ?? 0),
          )}
          onRemove={(id) => handleRowCheckboxChange(false, id as string)}
          requiredARIALabel={commonARIALabel.required}
        />
      </MuiGrid>

      <MuiBox className="table-container">
        <MuiGrid container data-testid="existing-deliverables">
          <MuiGrid size={{ xs: 12 }} className="header-right">
            <MuiTable
              ref={tableRef}
              pageId={pageId}
              tableHeaderForm={tableHeaderForm}
              columns={columns}
              tableResults={[...existingDeliverablesList]}
              noDataInApi={noDataInApi}
              noResultMessage={commonLabels.noResult}
              emptyMessage={commonLabels.noResult}
              priorityResults={[]}
            />
          </MuiGrid>
        </MuiGrid>
      </MuiBox>
    </ProjectExistingDeliverableWrapper>
  );
};

export default ProjectExistingDeliverables;
